import DeleteIcon from '@mui/icons-material/Delete';
import { Table } from '@startuptools/ui/Table';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { IconButton } from '@startuptools/ui/IconButton';
import { Sheet } from '@startuptools/ui/Sheet';
import { GenericError } from '@startuptools/ui/Error';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Checkbox } from '@startuptools/ui/Checkbox';
import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Snackbar } from '@startuptools/ui/Snackbar';
import { Trans, useLingui } from '@lingui/react';
import { StakeKind } from '@startuptools/common/cap-table';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { StakeTableRow } from './Row';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import {
  type GqlCapTableStake,
  type GqlCapTableStakeholder,
  type GqlCreateIdentityInput,
} from '../../../graphql/base-types.graphql';
import {
  CapTableDocument,
  useCapTableStakeDeleteMutation,
  useCapTableStakeSetEnabledMutation,
  useCapTableStakeUpdateMutation,
  useCreateCapTableStakeMutation,
} from '../cap-table.graphql';
import { StakeForm } from './Form';
import React from 'react';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { BigNumber } from 'bignumber.js';

const StakeKindLabel = ({ kind }: { kind: StakeKind }) => {
  switch (kind) {
    case StakeKind.Options:
      return <Trans id="Options" />;
    case StakeKind.Shares:
      return <Trans id="Shares" />;

    case StakeKind.Wise:
      return <Trans id="Wise" />;
  }
};

type Stake = Pick<
  GqlCapTableStake,
  | 'id'
  | 'name'
  | 'kind'
  | 'enabled'
  | 'stake'
  | 'value'
  | 'acqCost'
  | 'profit'
  | 'comment'
  | 'manualEntry'
  | 'wiseQuantity'
>;
export const StakeTable = ({
  stakes,
  identity,
  capTableId,
  defaultAddState = false,
  onCancelEdit,
  onCreateStake,
  stakeholder,
}: {
  stakes: Stake[];
  stakeholder: Pick<GqlCapTableStakeholder, 'stake'>;
  identity: GqlCreateIdentityInput;
  capTableId: string;
  defaultAddState?: boolean;
  onCancelEdit?: () => void;
  onCreateStake?: (stake: Stake) => void;
}) => {
  const [addNew, setAddNew] = React.useState(defaultAddState);
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  // eslint-disable-next-line sut/loading-mutation
  const [setEnabled] = useCapTableStakeSetEnabledMutation({ refetchQueries: [CapTableDocument] });

  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  // eslint-disable-next-line sut/loading-mutation
  const [deleteStake, { error: deleteStakeError, reset: resetDeleteCapTableStake }] = useCapTableStakeDeleteMutation({
    refetchQueries: [CapTableDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [updateStake] = useCapTableStakeUpdateMutation();
  const [editId, setEditId] = React.useState<string | null>(null);
  // eslint-disable-next-line sut/loading-mutation
  const [createCapTableStake, { error: createError, reset: resetCreate }] = useCreateCapTableStakeMutation({
    refetchQueries: [CapTableDocument],
  });
  const handleChecked = React.useCallback(
    (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      void setEnabled({ variables: { companyId, input: { id, enabled } } });
    },
    [companyId, setEnabled],
  );
  const stakeToBeDeleted = deleteId ? stakes.find(r => r.id === deleteId) : undefined;
  const hasWise = stakes.some(stake => stake.kind === StakeKind.Wise);
  return (
    <Sheet variant="soft" sx={{ py: 2, pl: 10, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
      <Box>
        <Button
          variant="outlined"
          onClick={() => {
            setAddNew(true);
          }}
        >
          <Trans id="Add stake" />
        </Button>
      </Box>

      {deleteId && (
        <ConfirmDialog
          header={<Trans id="Do you really want to delete this stake?" />}
          confirmProps={{ children: <Trans id="Delete" /> }}
          onCancel={() => {
            resetDeleteCapTableStake();
            setDeleteId(null);
          }}
          onConfirm={async () => {
            await deleteStake({ variables: { companyId, input: { id: deleteId } } });
            setDeleteId(null);
          }}
        >
          {`${stakeToBeDeleted?.name}`}
          {deleteStakeError && <GenericError />}
        </ConfirmDialog>
      )}

      <Table size="sm">
        <thead>
          <StakeTableRow
            renderwiseQuantity={hasWise}
            slots={{
              toggle: null,
              name: <Trans id="Name" />,
              kind: <Trans id="Kind" />,
              wiseQuantity: <Trans id="WISE (quantity)" />,
              stake: <Trans id="Stake (quantity)" />,
              value: <Trans id="Value" />,
              acqCost: <Trans id="Purchase cost" />,
              profit: <Trans id="Profit" />,
              share: <Trans id="Share" />,
              comment: <Trans id="Comment" />,
              actions: null,
            }}
            component="th"
          />
        </thead>
        <tbody>
          {stakes.map(stake => {
            if (editId === stake.id) {
              return (
                <StakeForm
                  key={stake.id}
                  renderwiseQuantity={hasWise}
                  editableFields={stake.manualEntry ? undefined : ['stake', 'acqCost', 'comment']}
                  stake={stake}
                  close={() => {
                    setEditId(null);
                  }}
                  onSave={async ({ id, stake, acqCost, comment }) => {
                    await updateStake({
                      variables: {
                        companyId,
                        input: {
                          id,
                          stake,
                          acqCost,
                          comment,
                        },
                      },
                    });
                  }}
                />
              );
            }
            const share = new BigNumber(stake.stake).div(stakeholder.stake);
            return (
              <StakeTableRow
                key={stake.id}
                renderwiseQuantity={hasWise}
                slots={{
                  toggle: (
                    <Checkbox
                      sx={{ padding: 0 }}
                      size="sm"
                      checked={stake.enabled}
                      onChange={event => handleChecked(stake.id, event)}
                    />
                  ),

                  wiseQuantity:
                    stake.kind === StakeKind.Wise ? (
                      <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                        {stake.wiseQuantity}
                      </NumberFormat>
                    ) : null,

                  name: stake.name,
                  kind: <StakeKindLabel kind={stake.kind} />,
                  stake: (
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                      {stake.stake}
                    </NumberFormat>
                  ),
                  value: (
                    <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                      {stake.value}
                    </NumberFormat>
                  ),
                  acqCost: (
                    <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                      {stake.acqCost}
                    </NumberFormat>
                  ),
                  profit: (
                    <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                      {stake.profit}
                    </NumberFormat>
                  ),
                  share: stake.enabled && (
                    <NumberFormat locale={i18n.locale} variant="percentage">
                      {share}
                    </NumberFormat>
                  ),
                  comment: stake.comment,
                  actions: (
                    <ButtonGroup>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setEditId(stake.id);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="danger"
                        onClick={() => {
                          setDeleteId(stake.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ButtonGroup>
                  ),
                }}
                component="td"
              />
            );
          })}
          {addNew && (
            <StakeForm
              close={() => {
                setAddNew(false);
                onCancelEdit?.();
              }}
              renderwiseQuantity={hasWise}
              onSave={async stake => {
                const { data } = await createCapTableStake({
                  variables: {
                    companyId,
                    input: {
                      stake: stake.stake,
                      name: stake.name,
                      capTableId,
                      identityInput: identityToInput(identity),
                      kind: stake.kind,
                      comment: stake.comment,
                      acqCost: stake.acqCost,
                    },
                  },
                });

                if (data) {
                  onCreateStake?.(data.createCapTableStake);
                }
              }}
            />
          )}
        </tbody>
      </Table>
      <Snackbar open={Boolean(createError)} onClose={resetCreate}>
        <GenericError color="warning" sx={{ width: '100%' }} />
      </Snackbar>
    </Sheet>
  );
};
